// Ponte segura para recursos nativos do Capacitor (Android/iOS).
// Em navegador web, tudo vira no-op (ou usa fallback web) — o app continua igual.
//
// Recursos: notificações locais (com som do canal), vibração (haptics),
// status bar, splash screen, botão voltar do Android.

const ANDROID_CHANNEL_ID = "alugaja-default";

export function isNative() {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

export function getPlatform() {
  try {
    return (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform()) || "web";
  } catch {
    return "web";
  }
}

let _notifReady = false;
let _notifGranted = false;
let _pushInited = false;
let _pushMode = null;

// Diagnóstico de push (visível na tela de Perfil para depurar iOS no TestFlight).
let _pushDiag = {
  platform: "web",
  isNative: false,
  permission: "unknown",   // granted | denied | prompt | unknown
  token: null,             // token FCM (o mesmo enviado ao backend)
  registered: false,       // conseguiu enviar o token ao /push/register-device?
  error: null,             // mensagem de erro do fluxo nativo (antes era engolida)
  updatedAt: null,
};

function _setDiag(patch) {
  _pushDiag = { ..._pushDiag, ...patch, updatedAt: new Date().toISOString() };
  try { window.dispatchEvent(new CustomEvent("aj:push-diag", { detail: _pushDiag })); } catch { /* noop */ }
}

/** Retorna o estado atual do registro de push (para UI de diagnóstico). */
export function getPushDiagnostics() {
  return { ..._pushDiag };
}

/**
 * Inicializa o ambiente nativo: status bar, esconde splash, pede permissão de
 * notificação e cria o canal Android (com som + vibração). Seguro chamar no web.
 */
export async function initNative() {
  if (!isNative()) return;

  // Safe-area edge-to-edge (Android 15+): o plugin é habilitado via configuração
  // (capacitor.config) no load nativo. Aqui só reforçamos em runtime para recalcular
  // os insets assim que a webview monta. NÃO definimos cor de fundo das barras
  // (customColorsForSystemBars:false) para evitar a API descontinuada setStatusBarColor;
  // as barras ficam transparentes e os ícones são ajustados por *Content (não obsoleto).
  try {
    const { SafeArea } = await import("@capacitor-community/safe-area");
    await SafeArea.enable({
      config: {
        customColorsForSystemBars: false,
        statusBarContent: "light",     // ícones claros sobre o header azul
        navigationBarContent: "dark",  // ícones escuros sobre a barra branca
      },
    });
  } catch (e) { /* plugin ausente no web */ }

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch (e) { /* noop */ }

  // Botão voltar do Android: volta no histórico ou minimiza o app na raiz
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack || window.history.length > 1) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
    // Ao voltar pro app (resume), recarrega dados do usuário (ex.: KYC aprovado
    // pelo admin enquanto o app estava em segundo plano).
    App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) window.dispatchEvent(new Event("aj:app-resume"));
    });
    App.addListener("resume", () => {
      window.dispatchEvent(new Event("aj:app-resume"));
    });
    // Universal/App Links: link https abriu o app → navega pela rota (HashRouter)
    App.addListener("appUrlOpen", (ev) => {
      try { goDeepLink(ev?.url); } catch { /* noop */ }
    });
  } catch (e) { /* noop */ }

  // Teclado nativo: ao focar um campo, rola-o para a área visível (acima do teclado).
  // Combinado com Keyboard.resize:'native' (capacitor.config), evita campos escondidos.
  try {
    document.addEventListener("focusin", (ev) => {
      const el = ev.target;
      if (el && typeof el.matches === "function" && el.matches("input, textarea, select")) {
        setTimeout(() => {
          try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* noop */ }
        }, 320);
      }
    });
  } catch (e) { /* noop */ }

  await ensureNotificationPermission();
}

/**
 * Navega para uma rota interna a partir de um link de push/notificação/deep-link.
 * O app usa HashRouter, então convertemos qualquer caminho/URL absoluta para
 * `#/rota` e usamos window.location.hash (sem full reload — o router assume).
 */
export function goDeepLink(url) {
  if (!url) return;
  try {
    let route = String(url).trim();
    if (/^https?:\/\//i.test(route) || route.startsWith("capacitor:")) {
      const u = new URL(route);
      route = u.hash ? u.hash : (u.pathname || "/") + (u.search || "");
    }
    route = route.replace(/^#/, "");
    if (!route.startsWith("/")) route = "/" + route;
    // Universal Link de compartilhamento (OG dinâmico): /api/share/listing/{id} → /itens/{id}
    const shareMatch = route.match(/^\/api\/share\/listing\/([^/?#]+)/i);
    if (shareMatch) route = "/itens/" + shareMatch[1];
    if (route === "/" ) { window.location.hash = "/"; return; }
    window.location.hash = route;
  } catch { /* noop */ }
}

export async function ensureNotificationPermission() {
  if (!isNative()) return false;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display !== "granted") {
      perm = await LocalNotifications.requestPermissions();
    }
    _notifGranted = perm.display === "granted";
    if (_notifGranted && getPlatform() === "android" && !_notifReady) {
      // Canal com som + vibração (Android 8+)
      await LocalNotifications.createChannel({
        id: ANDROID_CHANNEL_ID,
        name: "AlugaJá",
        description: "Mensagens, locações e avisos",
        importance: 5, // HIGH (mostra heads-up + som)
        visibility: 1,
        sound: undefined, // som padrão do sistema
        vibration: true,
        lights: true,
        lightColor: "#FF6B35",
      });
      _notifReady = true;
    }
    // Toca a ação de abrir uma notificação → deep-link (rota HashRouter)
    LocalNotifications.addListener("localNotificationActionPerformed", (ev) => {
      try {
        const url = ev?.notification?.extra?.url;
        if (url) goDeepLink(url);
      } catch { /* noop */ }
    });
    return _notifGranted;
  } catch (e) {
    return false;
  }
}

/**
 * Dispara uma notificação local nativa imediatamente.
 * No web é no-op (lá usamos toast + Web Audio). `extra.url` permite deep-link ao tocar.
 */
export async function notifyNative({ title, body, url }) {
  if (!isNative()) return;
  try {
    if (!_notifGranted) {
      const ok = await ensureNotificationPermission();
      if (!ok) return;
    }
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.schedule({
      notifications: [
        {
          id: Math.floor(Date.now() % 2147483647),
          title: title || "AlugaJá",
          body: body || "",
          channelId: ANDROID_CHANNEL_ID,
          smallIcon: "ic_stat_icon",
          extra: url ? { url } : undefined,
        },
      ],
    });
  } catch (e) { /* noop */ }
}

/**
 * Pede permissão de notificação no NAVEGADOR (web/PC). Idealmente chamado a partir
 * de um gesto do usuário. No nativo é tratado por ensureNotificationPermission().
 */
export async function ensureWebNotificationPermission() {
  try {
    if (isNative() || typeof window === "undefined" || !("Notification" in window)) return false;
    if (Notification.permission === "granted") return true;
    if (Notification.permission === "denied") return false;
    const p = await Notification.requestPermission();
    return p === "granted";
  } catch { return false; }
}

/**
 * Notificação do navegador (web/PC) — aparece no SO mesmo com a aba em 2º plano.
 * Ao clicar, foca a janela e navega via deep-link (HashRouter).
 */
export function notifyWeb({ title, body, url }) {
  try {
    if (isNative() || typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const n = new Notification(title || "AlugaJá", {
      body: body || "",
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      tag: "alugaja-notif",
      renotify: true,
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* noop */ }
      if (url) goDeepLink(url);
      n.close();
    };
  } catch { /* noop */ }
}

/** Notificação do SO independente da plataforma: nativo (Capacitor) ou navegador (web). */
export async function notifyOS(opts) {
  if (isNative()) return notifyNative(opts);
  return notifyWeb(opts);
}

/**
 * Registra o dispositivo no FCM (push remoto — funciona com o app fechado).
 * Chamar após o login. Envia o token ao backend (/api/push/register-device).
 * No web é no-op. `apiPost(path, body)` = função que faz POST autenticado.
 */
export async function initPush(apiPost, opts = {}) {
  const anonymous = !!opts.anonymous;
  const mode = anonymous ? "anon" : "auth";
  if (!isNative()) { _setDiag({ platform: getPlatform(), isNative: false, error: "não é app nativo (web)" }); return; }
  // Já registrado neste modo? Se mudou de anônimo → logado, re-registra p/ vincular o token à conta.
  if (_pushInited && _pushMode === mode) return;
  _pushInited = true;
  _pushMode = mode;
  _setDiag({ platform: getPlatform(), isNative: true, error: null });
  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    // V17.10.1 — NÃO chamar removeAllListeners() aqui: em Android/iOS ele remove
    // listeners internos do plugin (APNs delegate/FCM callbacks) e o getToken() abaixo
    // passa a devolver vazio, matando push. Usamos o guard `_pushInited` + `_pushMode`
    // (linhas 259-261) para não registrar listeners duplicados.

    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive !== "granted") {
      perm = await FirebaseMessaging.requestPermissions();
    }
    _setDiag({ permission: perm?.receive || "unknown" });
    if (perm.receive !== "granted") { _pushInited = false; _setDiag({ error: "permissão de notificação negada" }); return; }

    // Canal Android (som/vibração/luz personalizados). No iOS é ignorado.
    if (getPlatform() === "android") {
      try {
        await FirebaseMessaging.createChannel({
          id: "alugaja-default",
          name: "AlugaJá",
          description: "Mensagens, locações e avisos",
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: "#FF6B35",
        });
      } catch (e) { /* canal já existe */ }
    }

    // V17.10.1 — Endpoint é decidido a CADA envio (não capturado em closure) para
    // que, ao transicionar anônimo→autenticado, o token vá para o endpoint correto
    // mesmo se o listener antigo ainda estiver ativo. Isso evita ter que chamar
    // removeAllListeners() (que quebra o token entrega no iOS/Android).
    const _sendToken = async (token) => {
      if (!token) { _setDiag({ error: "getToken retornou vazio (APNs ainda não pronto no iOS?)" }); return; }
      _setDiag({ token, error: null });
      const currentEndpoint = _pushMode === "anon" ? "/push/register-anonymous" : "/push/register-device";
      try {
        await apiPost(currentEndpoint, { token, platform: getPlatform() });
        _setDiag({ registered: true });
      } catch (e) {
        _setDiag({ registered: false, error: "falha ao enviar token ao backend: " + String(e?.message || e) });
      }
    };

    await FirebaseMessaging.addListener("tokenReceived", (ev) => { _sendToken(ev?.token); });
    await FirebaseMessaging.addListener("notificationActionPerformed", (ev) => {
      try {
        const url = ev?.notification?.data?.url;
        if (url) goDeepLink(url);
      } catch { /* noop */ }
    });

    const { token } = await FirebaseMessaging.getToken();
    await _sendToken(token);
  } catch (e) {
    _pushInited = false;
    _setDiag({ error: "erro no plugin de push: " + String(e?.message || e) });
  }
}

/**
 * Reexecuta o registro de push sob demanda (botão de diagnóstico). Ao contrário de
 * initPush, ignora o guard _pushInited para permitir re-tentar após um erro.
 */
export async function refreshPushToken(apiPost) {
  _pushInited = false;
  await initPush(apiPost);
  return getPushDiagnostics();
}

/**
 * Compartilha conteúdo usando a folha de compartilhamento nativa (Capacitor Share) no
 * app, ou navigator.share no navegador. Fallback: copia o link para a área de
 * transferência. Retorna { ok, method } — method: 'native' | 'web' | 'clipboard' | 'none'.
 */
export async function shareContent({ title, text, url, dialogTitle }) {
  // Normaliza o scheme: o Share nativo rejeita URL sem http(s) ("Unsupported url").
  if (url && !/^https?:\/\//i.test(url)) url = "https://" + url.replace(/^\/+/, "");
  // Evita link DUPLICADO: se o texto já contém a URL, não a enviamos separada
  // (WhatsApp/Telegram concatenam text + url e o link apareceria 2x).
  const payloadUrl = (url && text && text.includes(url)) ? undefined : url;
  if (isNative()) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title, text, url: payloadUrl, dialogTitle: dialogTitle || title });
      return { ok: true, method: "native" };
    } catch (e) {
      if (String(e?.message || e).toLowerCase().includes("cancel")) return { ok: false, method: "native" };
      /* cai no fallback */
    }
  }
  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share({ title, text, url: payloadUrl });
      return { ok: true, method: "web" };
    }
  } catch (e) {
    if (e?.name === "AbortError") return { ok: false, method: "web" };
    /* cai no fallback */
  }
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && (url || text)) {
      await navigator.clipboard.writeText(url || text);
      return { ok: true, method: "clipboard" };
    }
  } catch { /* noop */ }
  return { ok: false, method: "none" };
}

function _blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Compartilha uma IMAGEM gerada (canvas/blob). No app nativo grava no cache e usa a
 * folha de compartilhamento do Capacitor (Share + Filesystem). No navegador usa
 * navigator.share com arquivo; fallback final = download da imagem.
 * Retorna { ok, method } — method: 'native' | 'web' | 'download' | 'none'.
 * Cache do app não exige permissão de armazenamento no Android/iOS.
 */
export async function shareImageFile({ blob, dataUrl, text, url, title = "AlugaJá", fileName = "alugaja.png" }) {
  if (url && !/^https?:\/\//i.test(url)) url = "https://" + url.replace(/^\/+/, "");
  if (isNative()) {
    let Share;
    try { ({ Share } = await import("@capacitor/share")); } catch { Share = null; }
    // 1) Tenta compartilhar a IMAGEM (precisa do @capacitor/filesystem no build nativo).
    try {
      const { Filesystem, Directory } = await import("@capacitor/filesystem");
      const base64 = dataUrl ? (dataUrl.split(",")[1] || "") : (blob ? await _blobToBase64(blob) : "");
      await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache, recursive: true });
      const { uri } = await Filesystem.getUri({ path: fileName, directory: Directory.Cache });
      if (Share) await Share.share({ title, text, files: [uri], dialogTitle: title });
      return { ok: true, method: "native" };
    } catch (e) {
      if (String(e?.message || e).toLowerCase().includes("cancel")) return { ok: false, method: "native" };
      /* imagem falhou → abre a folha só com texto/link (garante abrir a tela de compartilhar) */
    }
    // 2) Fallback nativo: folha de compartilhamento com TEXTO + LINK (sem "download" falso).
    try {
      if (Share) {
        await Share.share({ title, text, url, dialogTitle: title });
        return { ok: true, method: "native-text" };
      }
    } catch (e) {
      if (String(e?.message || e).toLowerCase().includes("cancel")) return { ok: false, method: "native" };
    }
    return { ok: false, method: "none" };
  }
  try {
    const file = blob ? new File([blob], fileName, { type: "image/png" }) : null;
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title, text });
      return { ok: true, method: "web" };
    }
  } catch (e) {
    if (e?.name === "AbortError") return { ok: false, method: "web" };
  }
  try {
    const a = document.createElement("a");
    a.href = dataUrl || (blob ? URL.createObjectURL(blob) : "");
    a.download = fileName;
    a.click();
    return { ok: true, method: "download" };
  } catch { return { ok: false, method: "none" }; }
}

/**
 * Captura uma foto no app NATIVO usando @capacitor/camera. Mostra um diálogo para o
 * usuário escolher CÂMERA ou GALERIA e pede as permissões necessárias com o motivo
 * declarado (Info.plist iOS / manifest Android). Retorna um File (image/jpeg) ou null.
 * No web retorna null → o chamador usa o <input type="file"> normal.
 * Lança erro só em falha real (cancelamento retorna null).
 */
export async function capturePhoto(source = "prompt") {
  if (!isNative()) return null;
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  const srcMap = { camera: CameraSource.Camera, photos: CameraSource.Photos, prompt: CameraSource.Prompt };
  try {
    const perm = await Camera.checkPermissions();
    if (perm.camera !== "granted" || perm.photos !== "granted") {
      await Camera.requestPermissions({ permissions: ["camera", "photos"] });
    }
  } catch { /* alguns targets não expõem checkPermissions p/ ambos — segue */ }
  try {
    const photo = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: srcMap[source] || CameraSource.Prompt,
      promptLabelHeader: "Adicionar foto",
      promptLabelPhoto: "Escolher da galeria",
      promptLabelPicture: "Tirar foto",
      width: 1600,
      correctOrientation: true,
    });
    const dataUrl = photo?.dataUrl;
    if (!dataUrl) return null;
    const blob = await (await fetch(dataUrl)).blob();
    const type = blob.type || "image/jpeg";
    const ext = (type.split("/")[1] || "jpg").replace("jpeg", "jpg");
    return new File([blob], `foto-${Date.now()}.${ext}`, { type });
  } catch (e) {
    const msg = String(e?.message || e).toLowerCase();
    if (msg.includes("cancel")) return null;   // usuário cancelou
    throw e;
  }
}

/**
 * Seleção MÚLTIPLA de fotos da galeria nativa (iOS/Android) via @capacitor/camera.
 * Retorna um array de File. No web retorna [] (o chamador usa o <input multiple>).
 * `limit` = máximo de fotos (0 = sem limite no iOS). Cancelar → [].
 */
export async function pickPhotos(limit = 10) {
  if (!isNative()) return [];
  const { Camera } = await import("@capacitor/camera");
  try {
    const perm = await Camera.checkPermissions();
    if (perm.photos !== "granted") await Camera.requestPermissions({ permissions: ["photos"] });
  } catch { /* segue */ }
  try {
    const res = await Camera.pickImages({ quality: 80, limit: limit > 0 ? limit : undefined });
    const photos = res?.photos || [];
    const files = [];
    for (const p of photos) {
      try {
        const src = p.webPath || p.path;
        if (!src) continue;
        const blob = await (await fetch(src)).blob();
        const type = blob.type || `image/${(p.format || "jpeg").replace("jpg", "jpeg")}`;
        const ext = (type.split("/")[1] || "jpg").replace("jpeg", "jpg");
        files.push(new File([blob], `foto-${Date.now()}-${files.length}.${ext}`, { type }));
      } catch { /* pula foto com erro */ }
    }
    return files;
  } catch (e) {
    const msg = String(e?.message || e).toLowerCase();
    if (msg.includes("cancel")) return [];
    throw e;
  }
}


/**
 * Vibração curta. Usa Haptics no nativo; navigator.vibrate como fallback web (Android Chrome).
 * `style`: "light" | "medium" | "heavy".
 */
export async function vibrate(style = "medium") {
  if (isNative()) {
    try {
      const { Haptics, ImpactStyle, NotificationType } = await import("@capacitor/haptics");
      if (style === "heavy") {
        // Evento IMPORTANTE (ex.: aluguel a aceitar): padrão de alerta forte e perceptível
        try { await Haptics.notification({ type: NotificationType.Warning }); return; }
        catch { await Haptics.vibrate({ duration: 400 }); return; }
      }
      const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium };
      await Haptics.impact({ style: map[style] || ImpactStyle.Medium });
      return;
    } catch (e) { /* cai no fallback */ }
  }
  try {
    if (navigator.vibrate) navigator.vibrate(style === "heavy" ? [90, 60, 90, 60, 140] : 45);
  } catch { /* noop */ }
}


/**
 * Abre um arquivo (PDF/documento) baixado como blob. No app NATIVO o WebView não
 * renderiza blob:// em nova aba (fica em branco), então gravamos no cache e abrimos
 * pela folha de compartilhamento/visualizador nativo (Share + Filesystem). No navegador
 * abrimos em nova aba (fallback: download). Retorna { ok, method, error? }.
 */
export async function openPdfFile({ blob, dataUrl, fileName = "documento.pdf" }) {
  if (isNative()) {
    try {
      const { Filesystem, Directory } = await import("@capacitor/filesystem");
      const { Share } = await import("@capacitor/share");
      const base64 = dataUrl ? (dataUrl.split(",")[1] || "") : (blob ? await _blobToBase64(blob) : "");
      await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache, recursive: true });
      const { uri } = await Filesystem.getUri({ path: fileName, directory: Directory.Cache });
      await Share.share({ title: fileName, files: [uri], dialogTitle: "Abrir documento" });
      return { ok: true, method: "native" };
    } catch (e) {
      if (String(e?.message || e).toLowerCase().includes("cancel")) return { ok: false, method: "native" };
      return { ok: false, method: "none", error: String(e?.message || e) };
    }
  }
  try {
    const url = dataUrl || (blob ? URL.createObjectURL(blob) : "");
    const w = window.open(url, "_blank");
    if (!w) { const a = document.createElement("a"); a.href = url; a.download = fileName; a.click(); }
    if (blob) setTimeout(() => URL.revokeObjectURL(url), 60000);
    return { ok: true, method: "web" };
  } catch (e) { return { ok: false, method: "none", error: String(e?.message || e) }; }
}

/** Abre uma URL externa (loja de apps, etc.) fora do WebView. */
export async function openExternal(url) {
  if (!url) return;
  try { window.open(url, "_system"); }
  catch { try { window.location.href = url; } catch { /* noop */ } }
}

/**
 * Abre a loja de apps de forma CONFIÁVEL para a tela de atualização obrigatória.
 * Usa o esquema nativo da loja (market:// no Android, itms-apps:// no iOS) para abrir
 * o APP da loja direto; com fallback para o link https se o esquema não puder abrir.
 * Se não houver storeUrl (ex.: iOS ainda não publicado), cai numa BUSCA na loja.
 * No navegador (web/PC), abre o https em nova aba.
 */
export async function openAppStore({ storeUrl, platform } = {}) {
  const plat = platform || getPlatform();
  const https = (storeUrl || "").trim();
  const httpsFallback = https || (plat === "ios"
    ? "https://apps.apple.com/br/search?term=AlugaJa"
    : "https://play.google.com/store/apps/details?id=com.alugaja.app");
  // Esquema nativo da loja (abre o app da Play Store / App Store diretamente)
  let scheme = httpsFallback;
  if (plat === "android") {
    const m = httpsFallback.match(/[?&]id=([^&]+)/);
    scheme = "market://details?id=" + (m ? m[1] : "com.alugaja.app");
  } else if (plat === "ios") {
    scheme = httpsFallback.replace(/^https?:\/\//i, "itms-apps://");
  }
  if (!isNative()) { try { window.open(httpsFallback, "_blank"); } catch { /* noop */ } return; }
  try {
    // webpackChunkName garante um chunk separado; se o módulo não estiver instalado, o catch abaixo cobre.
    const mod = await import(/* webpackChunkName: "capacitor-app-launcher" */ "@capacitor/app-launcher");
    const AppLauncher = mod.AppLauncher;
    if (!AppLauncher) throw new Error("AppLauncher unavailable");
    let canOpen = false;
    try { canOpen = (await AppLauncher.canOpenUrl({ url: scheme }))?.value; } catch { canOpen = false; }
    await AppLauncher.openUrl({ url: canOpen ? scheme : httpsFallback });
  } catch {
    // Fallback final: system browser / navegação direta
    try { window.open(httpsFallback, "_system"); } catch { try { window.location.href = httpsFallback; } catch { /* noop */ } }
  }
}

function _cmpVersion(a, b) {
  const pa = String(a || "0").split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b || "0").split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] || 0, y = pb[i] || 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}

/**
 * Verifica se o app nativo precisa de ATUALIZAÇÃO OBRIGATÓRIA.
 * `fetchConfig` deve retornar o objeto de /api/app/version-config.
 * Retorna { platform, installed, min, storeUrl, message } se precisar atualizar; senão null.
 */
export async function checkForcedUpdate(fetchConfig) {
  if (!isNative()) return null;
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    const platform = getPlatform();
    const cfg = await fetchConfig();
    const p = cfg && cfg[platform];
    if (!p || !p.enforce || !p.min_version) return null;
    const installed = info?.version || "0";
    if (_cmpVersion(installed, p.min_version) < 0) {
      return { platform, installed, min: p.min_version, storeUrl: p.store_url || "", message: cfg.message || "" };
    }
    return null;
  } catch {
    return null; // em dúvida, NÃO bloqueia o usuário
  }
}
